import {
  GET_EVALUATION_REPORTS_BY_UUIDS,
  GET_METRICS_BY_IDS,
  TestDetailDrawer,
} from '@agilelab/plugin-wb-governance';
import {
  EvaluationReportByUUIDQuery,
  MetricItem as GovernanceMetricItem,
  MetricByIdQuery,
  MetricResult,
  Timing,
} from '@agilelab/plugin-wb-governance-common';
import {
  EnumFilter,
  TableCellProps,
  ThresholdResult,
  WbCardContent,
  WbTable,
  WbResponsiveTableToolbar,
  WbTruncatedTypographyWrapper,
  getObjectPropFromString,
  useDrawersContext,
} from '@agilelab/plugin-wb-platform';
import { useApolloClient } from '@apollo/client';
import { makeStyles, Paper, Typography } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';

export interface Filters {
  text?: string;
  result?: string[];
  timing?: string[];
}

const useStyles = makeStyles(() => ({
  rightContent: {
    overflowY: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  table: {
    flex: '1 1 0',
  },
}));

type MetricItem = GovernanceMetricItem & { id: string };

type FilterField = 'result' | 'timing';

const changeFilter = (
  field: FilterField,
  value: string[] | undefined,
  setFilters: (filters: Filters) => void,
  currentFilters: Filters,
) => {
  setFilters({ ...currentFilters, [field]: value });
};

const columns: TableCellProps<MetricItem>[] = [
  {
    field: 'name',
    fieldRender: (field: any) => (
      <WbTruncatedTypographyWrapper
        value={`${field.name} v:${field.version}`}
      />
    ),
    headerName: 'Metric',
    width: '20%',
    sortable: true,
  },
  {
    field: 'timing',
    fieldRender: (field: any) => (
      <WbTruncatedTypographyWrapper value={field.timing} />
    ),
    headerName: 'Timing',
    width: '15%',
    sortable: true,
  },
  {
    field: 'description',
    fieldRender: (field: any) => (
      <WbTruncatedTypographyWrapper value={field.description} />
    ),
    headerName: 'Description',
    width: '20%',
    sortable: true,
  },
  {
    field: 'date',
    fieldRender: field => (
      <Typography component="div">
        {format(parseISO(field.date ?? ''), 'yyyy/MM/dd HH:mm:ss')}
      </Typography>
    ),
    headerName: 'Date',
    width: '15%',
  },
  {
    field: 'result',
    fieldRender: (field: any) => {
      return (
        <ThresholdResult
          value={field.value}
          result={field.result}
          hasError={field.error}
        />
      );
    },
    headerName: 'Result',
    width: '30%',
    sortable: true,
  },
];

export const MetricsTable = ({
  metricResults,
}: {
  metricResults: MetricResult[];
}) => {
  const classes = useStyles();
  const apolloClient = useApolloClient();
  const { drawers, toggleBoolean, isOpen } = useDrawersContext();
  const [evaluationId, setEvaluationId] = useState<string>();
  const [filters, setFilters] = useState<Filters>({});
  const [pagination, setPagination] = useState({
    limit: 25,
    offset: 0,
  });

  const { value: data, loading } = useAsync(async () => {
    if (!isOpen || !metricResults.length) return [];

    const uniqueMetricIds = [...new Set(metricResults.map(r => r.metricId))];
    const uniqueReportIds = [...new Set(metricResults.map(r => r.reportId))];

    const [metricsResponse, reportsResponse] = await Promise.all([
      apolloClient.query<MetricByIdQuery>({
        query: GET_METRICS_BY_IDS,
        variables: { metricIds: uniqueMetricIds },
        fetchPolicy: 'cache-first',
      }),
      apolloClient.query<EvaluationReportByUUIDQuery>({
        query: GET_EVALUATION_REPORTS_BY_UUIDS,
        variables: { uuids: uniqueReportIds },
        fetchPolicy: 'cache-first',
      }),
    ]);

    const metricsMap = new Map(
      metricsResponse.data.cgp_governance_entity.map(metric => [
        metric.governance_entity_id,
        metric,
      ]),
    );

    const reportsMap = new Map(
      reportsResponse.data.cgp_evaluation_report.map(report => [
        report.uuid,
        report,
      ]),
    );

    return metricResults
      .map(result => {
        const metric = metricsMap.get(result.metricId);
        const report = reportsMap.get(result.reportId);

        if (!metric || !report) {
          return undefined;
        }

        return {
          id: result.evaluationResultId,
          name: metric.name,
          result: result.evaluationResult.thresholdResult,
          value: result.evaluationResult.value,
          description: metric.description,
          version: metric.version,
          error: !!result.evaluationResult.errors?.length,
          date: report.update_time,
          timing: metric.timing as Timing,
        } as MetricItem;
      })
      .filter(Boolean) as MetricItem[];
  }, [isOpen, metricResults, apolloClient]);

  const dataTable = useMemo(() => {
    if (!data) return [];

    const fields = columns.map(col => col.field || '');
    Object.keys(filters).forEach(key =>
      filters[key as keyof Filters] === undefined
        ? delete filters[key as keyof Filters]
        : {},
    );

    const filtered = data?.filter(row => {
      const rowItems = fields.map(field => getObjectPropFromString(row, field));

      return rowItems.find(
        item => item && item.includes(filters?.text?.toLowerCase() ?? ''),
      );
    });

    let filteredData = filtered;
    if (filters.timing) {
      const filterTimings = (filters.timing || []).map(tim =>
        _.capitalize(tim.toLocaleLowerCase()),
      );

      filteredData = filteredData.filter(row => {
        const rowTiming = _.capitalize(row.timing?.toLocaleLowerCase() ?? '');

        const timingMatches =
          filterTimings.length === 0 || filterTimings.includes(rowTiming);

        return timingMatches;
      });
    }

    return filteredData;
  }, [data, filters]);

  const timingOptions = ['Runtime', 'Deployment'];

  const filterConfigurations = [
    {
      field: 'Timing',
      options: timingOptions,
      value: filters.timing,
      onChange: (timing: string[] | undefined) =>
        changeFilter('timing', timing, setFilters, filters),
      onSearch: (v: string | RegExp) =>
        timingOptions.filter(o => new RegExp(v, 'ig').test(o)),
    },
  ];

  return (
    <>
      <Paper style={{ height: '100%', overflow: 'hidden', flex: '1 1 0' }}>
        <WbCardContent className={classes.rightContent}>
          <WbResponsiveTableToolbar
            searchProps={{
              value: filters.text,
              onChange: search => {
                setFilters({ text: search });
              },
            }}
            onClear={() => setFilters({})}
            marginBottom
          >
            <WbResponsiveTableToolbar.Filters>
              {filterConfigurations.map((config, index) => (
                <EnumFilter<string>
                  key={index}
                  field={config.field}
                  options={config.options}
                  onChange={config.onChange}
                  value={config.value}
                  renderOption={(o: any) => o}
                  renderValue={(o: any) => o}
                  onSearch={config.onSearch}
                />
              ))}
            </WbResponsiveTableToolbar.Filters>
          </WbResponsiveTableToolbar>
          <WbTable<MetricItem>
            classNames={{ container: classes.table }}
            components={{
              tableLoader: { loading },
              tableContent: {
                columns,
                rows: dataTable.sort(
                  (a, b) =>
                    new Date(b.date ?? 0).getTime() -
                    new Date(a.date ?? 0).getTime(),
                ),
                onRowClick: e => {
                  setEvaluationId(e.id);
                  toggleBoolean(1);
                },
              },
            }}
            pagination={{
              count: dataTable?.length || 0,
              limit: pagination.limit,
              offset: pagination.offset,
              onPageChange: (page: number) => {
                setPagination({
                  ...pagination,
                  offset: page * pagination.limit,
                });
              },
              onRowsPerPageChange: (rowsPerPage: number) => {
                setPagination({
                  offset: 0,
                  limit: rowsPerPage,
                });
              },
            }}
          />

          <TestDetailDrawer
            evaluationId={evaluationId!}
            idType="external"
            open={drawers.get(1) ?? false}
            setOpen={() => toggleBoolean(1)}
          />
        </WbCardContent>
      </Paper>
    </>
  );
};
